<template>
  <ApiResourceCard
    class="api-key-card"
    :name="apiKey.name"
    :status="status"
    :status-text="statusText"
  >
    <template v-if="canRevoke" #actions>
      <ApiResourceCardActions
        :resource-name="apiKey.name"
        :menu-options="REVOKE_MENU_OPTIONS"
        @menu-select="handleMenuSelect"
      />
    </template>
    <ApiMetaList>
      <ApiMetaItem label="Created" :value="createdLabel" />
      <ApiMetaItem label="Last used" :value="lastUsedLabel" />
      <ApiMetaItem label="Expires">
        <span :class="expiresClass">{{ expiresLabel }}</span>
      </ApiMetaItem>
      <ApiMetaItem label="Created by" :value="apiKey.created_by_user_email" />
      <ApiKeyCardValidity
        v-if="expiresAt"
        :label="validityLabel"
        :percent="validityPercent"
        :color="validityColor"
      />
    </ApiMetaList>
    <template #footer>
      <div class="api-key-card__summary">
        <BittsTag
          v-for="chip in summaryChips"
          :key="chip"
          :text="chip"
          color="neutral"
          size="small"
        />
      </div>
      <BittsButton
        :text="isExpanded ? 'Hide Details' : 'View Details'"
        type="primary"
        variant="ghost"
        size="small"
        :right-icon="['fak', isExpanded ? 'chevron-up' : 'chevron-down']"
        @click="isExpanded = !isExpanded"
      />
    </template>
    <template v-if="isExpanded" #expansion>
      <section class="api-key-card__detail">
        <h4 class="api-key-card__detail-heading">
          Scopes
          <BittsCount
            :count="apiKey.scopes.length"
            type="neutral"
            :show-zero="true"
          />
        </h4>
        <div v-if="apiKey.scopes.length" class="api-key-card__chips">
          <BittsTag
            v-for="scope in apiKey.scopes"
            :key="scope"
            :text="scope"
            color="neutral"
            size="small"
          />
        </div>
        <p v-else class="api-key-card__detail-text">No scopes</p>
      </section>
      <section class="api-key-card__detail">
        <h4 class="api-key-card__detail-heading">Allowed IPs</h4>
        <div v-if="allowedIps.length" class="api-key-card__chips">
          <BittsTag
            v-for="ip in allowedIps"
            :key="ip"
            :text="ip"
            color="neutral"
            size="small"
          />
        </div>
        <p v-else class="api-key-card__detail-text">All IPs allowed</p>
      </section>
      <section class="api-key-card__detail">
        <h4 class="api-key-card__detail-heading">
          Routes
          <BittsCount
            :count="allowedRoutes.length"
            type="neutral"
            :show-zero="true"
          />
        </h4>
        <div v-if="allowedRoutes.length" class="api-key-card__chips">
          <BittsTag
            v-for="route in allowedRoutes"
            :key="route"
            :text="route"
            color="neutral"
            size="small"
          />
        </div>
        <p v-else class="api-key-card__detail-text">All routes allowed</p>
      </section>
    </template>
  </ApiResourceCard>
</template>

<script setup lang="ts">
import { BittsButton, BittsCount, BittsTag } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed, ref } from 'vue';

import type { ApiKey } from '@/types/api_keys';

import type { ApiCardMenuOptionType, ApiResourceStatusType } from '../types';

import ApiKeyCardValidity from './ApiKeyCardValidity.vue';
import ApiMetaItem from './ApiMetaItem.vue';
import ApiMetaList from './ApiMetaList.vue';
import ApiResourceCard from './ApiResourceCard.vue';
import ApiResourceCardActions from './ApiResourceCardActions.vue';

const { apiKey, canRevoke = false } = defineProps<{
  apiKey: ApiKey;
  canRevoke?: boolean;
}>();

const emit = defineEmits<{
  revoke: [];
}>();

const EXPIRING_SOON_DAYS = 7;

const REVOKE_MENU_OPTIONS: ApiCardMenuOptionType[] = [
  { label: 'Revoke key', value: 'revoke', faIcon: ['fas', 'trash-can'] },
];

const isExpanded = ref(false);

const createdAt = computed(() => DateTime.fromISO(apiKey.created_at));

const expiresAt = computed(() =>
  apiKey.expires_at
    ? DateTime.fromISO(apiKey.expires_at, { zone: 'utc' })
    : null,
);

const isExpired = computed(
  () => !!expiresAt.value && expiresAt.value < DateTime.utc(),
);

const daysLeft = computed(() => {
  if (!expiresAt.value || isExpired.value) return 0;
  const days = expiresAt.value
    .startOf('day')
    .diff(DateTime.utc().startOf('day'), 'days').days;
  return Math.max(0, Math.round(days));
});

const isExpiringSoon = computed(
  () =>
    !!expiresAt.value &&
    !isExpired.value &&
    daysLeft.value < EXPIRING_SOON_DAYS,
);

const status = computed<ApiResourceStatusType>(() =>
  isExpired.value ? 'danger' : isExpiringSoon.value ? 'warning' : 'success',
);

const statusText = computed(
  () =>
    ({
      danger: 'Expired',
      warning: 'Expiring soon',
      success: 'Active',
      neutral: '',
    })[status.value],
);

const createdLabel = computed(() =>
  createdAt.value.toLocaleString(DateTime.DATE_MED),
);

const lastUsedLabel = computed(() => {
  if (!apiKey.last_used_at) return 'Never';
  const relative = DateTime.fromISO(apiKey.last_used_at).toRelative();
  return !relative || relative.startsWith('in') ? 'Just now' : relative;
});

const expiresLabel = computed(
  () => expiresAt.value?.toLocaleString(DateTime.DATE_MED) ?? 'Never',
);

const expiresClass = computed(
  () =>
    ({
      danger: 'text-danger-text',
      warning: 'text-warning-text',
      success: undefined,
      neutral: undefined,
    })[status.value],
);

const validityPercent = computed(() => {
  if (!expiresAt.value || isExpired.value) return 0;
  const lifetimeDays = expiresAt.value.diff(createdAt.value, 'days').days;
  if (lifetimeDays <= 0) return 0;
  return Math.min(100, Math.round((daysLeft.value / lifetimeDays) * 100));
});

const validityLabel = computed(() =>
  isExpired.value ? 'Expired' : `${pluralize(daysLeft.value, 'day')} left`,
);

const validityColor = computed(() =>
  isExpired.value ? 'danger' : isExpiringSoon.value ? 'warning' : 'secondary',
);

const allowedIps = computed(() => apiKey.allowed_ips ?? []);
const allowedRoutes = computed(() => apiKey.allowed_routes ?? []);

const summaryChips = computed(() => [
  pluralize(apiKey.scopes.length, 'scope'),
  allowedIps.value.length
    ? pluralize(allowedIps.value.length, 'IP')
    : 'All IPs',
  allowedRoutes.value.length
    ? pluralize(allowedRoutes.value.length, 'route')
    : 'All routes',
]);

function handleMenuSelect(value: string) {
  if (value === 'revoke') emit('revoke');
}

function pluralize(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-key-card__summary {
  @apply flex items-center gap-8 flex-wrap;
}

.api-key-card__detail {
  @apply flex flex-col gap-8;
}

.api-key-card__detail-heading {
  @apply flex items-center gap-8 text-base font-bold text-neutral-text-strong m-0;
}

.api-key-card__detail-text {
  @apply text-base text-neutral-text m-0;
}

.api-key-card__chips {
  @apply flex flex-wrap gap-8;
}
</style>
